import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { ConfirmModalComponent } from '../../../modals/confirm-modal/confirm-modal.component';
import { ToastrService } from 'ngx-toastr';

export interface MailFolderWithCount {
  idMailFolder: number;
  name: string;
  sortOrder: number;
  isActive: boolean;
  organizationId?: number;
  operatorId: number;
  dateReg: Date;
  messageCount: number;
}

@Component({
  selector: 'app-folder-management-modal',
  templateUrl: './folder-management-modal.component.html',
  styleUrls: ['./folder-management-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FolderManagementModalComponent implements OnInit {
  public folders: MailFolderWithCount[] = [];
  public newFolderName: string = '';
  public editingFolderId: number | null = null;
  public editingFolderName: string = '';
  public isLoading: boolean = false;
  public isSaving: boolean = false;
  public errorMessage: string = '';

  public onClose: Subject<boolean> = new Subject();

  private readonly apiUrl = '/api/MailFolder';

  constructor(
    public activeModal: NgbActiveModal,
    private http: HttpClient,
    private changeDetectorRef: ChangeDetectorRef,
    private modalService: NgbModal,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadFolders();
  }

  loadFolders(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.changeDetectorRef.markForCheck();

    this.http.get<MailFolderWithCount[]>(`${this.apiUrl}/all`).subscribe({
      next: (folders) => {
        this.folders = folders;
        this.isLoading = false;
        this.changeDetectorRef.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load folders:', error);
        this.errorMessage = 'Грешка при зареждане на папките';
        this.isLoading = false;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  createFolder(): void {
    if (!this.newFolderName.trim()) {
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';
    this.changeDetectorRef.markForCheck();

    const folder = {
      name: this.newFolderName.trim(),
      isActive: true
    };

    this.http.post<boolean>(`${this.apiUrl}/create`, folder).subscribe({
      next: (success) => {
        if (success) {
          this.newFolderName = '';
          this.loadFolders();
          this.toastr.success(`Папка "${folder.name}" е създадена успешно`, 'Успех', {
            timeOut: 3000,
            closeButton: true
          });
        } else {
          this.errorMessage = 'Неуспешно създаване на папка';
        }
        this.isSaving = false;
        this.changeDetectorRef.markForCheck();
      },
      error: (error) => {
        console.error('Failed to create folder:', error);
        this.errorMessage = 'Грешка при създаване на папка';
        this.isSaving = false;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  startEdit(folder: MailFolderWithCount): void {
    this.editingFolderId = folder.idMailFolder;
    this.editingFolderName = folder.name;
    this.changeDetectorRef.markForCheck();
  }

  saveEdit(): void {
    if (!this.editingFolderName.trim() || this.editingFolderId === null) {
      return;
    }

    const folder = this.folders.find(f => f.idMailFolder === this.editingFolderId);
    if (!folder) {
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';
    this.changeDetectorRef.markForCheck();

    const updateModel = {
      idMailFolder: this.editingFolderId,
      name: this.editingFolderName.trim(),
      sortOrder: folder.sortOrder,
      isActive: folder.isActive
    };

    this.http.post<boolean>(`${this.apiUrl}/update`, updateModel).subscribe({
      next: (success) => {
        if (success) {
          this.cancelEdit();
          this.loadFolders();
        } else {
          this.errorMessage = 'Неуспешно обновяване на папка';
        }
        this.isSaving = false;
        this.changeDetectorRef.markForCheck();
      },
      error: (error) => {
        console.error('Failed to update folder:', error);
        this.errorMessage = 'Грешка при обновяване на папка';
        this.isSaving = false;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  cancelEdit(): void {
    this.editingFolderId = null;
    this.editingFolderName = '';
    this.changeDetectorRef.markForCheck();
  }

  deleteFolder(folder: MailFolderWithCount): void {
    if (folder.messageCount > 0) {
      return;
    }

    // Open confirmation dialog
    const confirmModalRef = this.modalService.open(ConfirmModalComponent, {
      centered: true,
      backdrop: 'static'
    });

    confirmModalRef.componentInstance.title = 'Изтриване на папка';
    confirmModalRef.componentInstance.message = `Сигурни ли сте, че искате да изтриете папка "${folder.name}"?`;
    confirmModalRef.componentInstance.confirmText = 'Изтрий';
    confirmModalRef.componentInstance.cancelText = 'Отказ';
    confirmModalRef.componentInstance.confirmButtonClass = 'btn-danger';

    confirmModalRef.componentInstance.onClose.subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.performDeleteFolder(folder);
      }
    });
  }

  private performDeleteFolder(folder: MailFolderWithCount): void {
    this.isSaving = true;
    this.errorMessage = '';
    this.changeDetectorRef.markForCheck();

    this.http.post<boolean>(`${this.apiUrl}/remove/${folder.idMailFolder}`, {}).subscribe({
      next: (success) => {
        if (success) {
          this.loadFolders();
          this.toastr.success(`Папка "${folder.name}" е изтрита успешно`, 'Успех', {
            timeOut: 3000,
            closeButton: true
          });
        } else {
          this.errorMessage = 'Неуспешно изтриване на папка';
        }
        this.isSaving = false;
        this.changeDetectorRef.markForCheck();
      },
      error: (error) => {
        console.error('Failed to delete folder:', error);
        this.errorMessage = 'Грешка при изтриване на папка';
        this.isSaving = false;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  close(): void {
    this.onClose.next(true);
    this.activeModal.close(true);
  }
}
